
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useStaticCart } from "@/contexts/StaticCartContext";
import { PaymentMethodModal } from "./PaymentMethodModal";
import { PaymentLoadingModal } from "./PaymentLoadingModal";

import { PixPaymentModal } from "./PixPaymentModal";
import { CountdownTimer } from "./CountdownTimer";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/priceUtils";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import { useAuth } from "@/hooks/useAuth";

export function StaticCartSummary() {
  const { items, subtotal, discount, total, bonusCount, originalTotal } = useStaticCart();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [customerData, setCustomerData] = useState<any>(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<'pix' | 'cartao_credito' | null>(null);
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isPixModalOpen, setIsPixModalOpen] = useState(false);
  const [pixData, setPixData] = useState<any>(null);

  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  // Escutar status do pedido específico
  usePaymentStatus({
    orderId: currentOrderId,
    onStatusChange: async (status) => {
      console.log('Status do pagamento mudou:', status, 'para pedido:', currentOrderId);
      
      if (status === 'pago' || status === 'pagamento_aprovado' || status === 'confirmado') {
        // ✅ Cancelar timeout de segurança se existir
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        // Fechar modal PIX se estiver aberto
        if (isPixModalOpen) {
          setIsPixModalOpen(false);
        }
        
        // Se estiver em loading, aguardar no mínimo 10 segundos
        if (isPaymentLoading && loadingStartTime) {
          const timeElapsed = Date.now() - loadingStartTime;
          const minLoadingTime = 10000; // 10 segundos
          
          if (timeElapsed < minLoadingTime) {
            setTimeout(async () => {
              // ✅ Re-validar status do pedido antes de redirecionar
              const { data: pedidoAtual } = await supabase
                .from('pedidos')
                .select('status, total')
                .eq('id', currentOrderId)
                .single();
              
              setIsPaymentLoading(false);
              
              if (pedidoAtual?.status === 'pago' || 
                  pedidoAtual?.status === 'pagamento_aprovado' ||
                  pedidoAtual?.status === 'confirmado') {
                navigate(`/obrigado-compra?pedido=${currentOrderId}&valor=${pedidoAtual.total}&origem=landing_page&rota=${encodeURIComponent(window.location.pathname)}&metodo=cartao_credito`);
              } else {
                // ❌ Status mudou para cancelado/recusado/etc
                navigate(`/app/confirmacao-pagamento?pedido=${currentOrderId}&status=aguardando&metodo=cartao`);
              }
              
              setCurrentOrderId(null);
            }, minLoadingTime - timeElapsed);
          } else {
            // ✅ Re-validar status do pedido antes de redirecionar
            const { data: pedidoAtual } = await supabase
              .from('pedidos')
              .select('status, total')
              .eq('id', currentOrderId)
              .single();
            
            setIsPaymentLoading(false);
            
            if (pedidoAtual?.status === 'pago' || 
                pedidoAtual?.status === 'pagamento_aprovado' ||
                pedidoAtual?.status === 'confirmado') {
              navigate(`/obrigado-compra?pedido=${currentOrderId}&valor=${pedidoAtual.total}&origem=landing_page&rota=${encodeURIComponent(window.location.pathname)}&metodo=cartao_credito`);
            } else {
              // ❌ Status mudou para cancelado/recusado/etc
              navigate(`/app/confirmacao-pagamento?pedido=${currentOrderId}&status=aguardando&metodo=cartao`);
            }
            
            setCurrentOrderId(null);
          }
        } else {
          // Para PIX, buscar dados do pedido e redirecionar para página de sucesso
          const { data: pedidoAtual } = await supabase
            .from('pedidos')
            .select('total')
            .eq('id', currentOrderId)
            .single();
          
          navigate(`/obrigado-compra?pedido=${currentOrderId}&valor=${pedidoAtual?.total || 0}&origem=landing_page&rota=${encodeURIComponent(window.location.pathname)}&metodo=pix`);
          setCurrentOrderId(null);
        }
      }
    }
  });

  const handleCheckout = () => {
    setIsPaymentModalOpen(true);
  };

  const handlePaymentConfirm = async (paymentData: any) => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    setIsPaymentModalOpen(false);
    
    // Configurar método de pagamento e ativar loading para cartão
    setCurrentPaymentMethod(paymentData.metodoPagamento);
    if (paymentData.metodoPagamento === 'cartao_credito') {
      setIsPaymentLoading(true);
      setLoadingStartTime(Date.now());
    }

    try {
      // ✅ CORRIGIDO - Mapear campos diretos do FormData
      const dadosCliente = {
        nome: paymentData.nome,
        email: paymentData.email,
        cpfCnpj: paymentData.cpf,
        telefone: paymentData.telefone,
        ...(paymentData.metodoPagamento === 'cartao_credito' && paymentData.cep && {
          endereco: {
            logradouro: paymentData.logradouro,
            numero: paymentData.numero,
            complemento: paymentData.complemento,
            bairro: paymentData.bairro,
            cep: paymentData.cep,
            cidade: paymentData.cidade,
            estado: paymentData.estado
          }
        })
      };

      const orderData = {
        items: items.map(item => ({
          id: item.id,
          title: item.title,
          price: parseFloat(item.originalPrice.replace(/R\$\s?/, '').replace(',', '.')), // Converter string para number
          imageUrl: item.imageUrl,
          quantity: 1,
          description: item.description
        })),
        subtotal: originalTotal,  // 503 - valor original dos produtos
        discount,                 // 306 - desconto aplicado
        total,                   // 197 - valor final a pagar
        metodo_pagamento: paymentData.metodoPagamento,
        dadosCliente,
        dadosCartao: paymentData.metodoPagamento === 'cartao_credito' ? {
          nomeCartao: paymentData.nomeCartao,
          numeroCartao: paymentData.numeroCartao,
          validadeCartao: paymentData.validadeCartao,
          cvvCartao: paymentData.cvvCartao
        } : null,

        installments: paymentData.installments || 1,
        isLandingPurchase: true,
        origem_rota: window.location.pathname,
        
      };

      console.log('Processando pagamento...', orderData);

      const { data, error } = await supabase.functions.invoke('create-asaas-payment', {
        body: orderData
      });

      if (error) {
        console.error('Erro na função:', error);
        throw new Error(error.message || 'Erro ao processar pagamento');
      }

      console.log('Resposta da função:', data);

      if (!data.success) {
        throw new Error(data.error || 'Erro ao processar pagamento');
      }

      if (data.payment?.billingType === 'PIX') {
        console.log('Pagamento PIX - abrindo modal PIX');
        
        // Configurar dados do PIX para o modal
        setPixData({
          qrCodeUrl: data.payment.pixQrCode,
          copyPasteCode: data.payment.pixCopyPaste,
          orderId: data.pedido.id,
          orderNumber: data.pedido.numero_pedido,
          total: total
        });
        
        // Salvar ID do pedido para monitorar via Realtime
        setCurrentOrderId(data.pedido.id);
        
        // Abrir modal PIX
        setIsPixModalOpen(true);
      } else {
        console.log('Pagamento cartão - aguardando confirmação via webhook');
        // Salvar ID do pedido para monitorar via Realtime
        setCurrentOrderId(data.pedido.id);

        // ✅ ADICIONAR AQUI:
        // Timeout de segurança
        timeoutRef.current = setTimeout(() => {
          if (isPaymentLoading) {
            console.log('Payment timeout reached, redirecting to waiting page');
            
            // ✅ NOTIFICAR ADMIN VIA EDGE FUNCTION
            supabase.functions.invoke('notify-payment-timeout', {
              body: {
                orderId: data.pedido.id,
                customerName: dadosCliente.nome,
                customerEmail: dadosCliente.email,
                customerPhone: dadosCliente.telefone,
                paymentValue: total,
                timeoutDuration: 20000
              }
            }).catch(error => {
              console.error('Erro ao notificar timeout:', error);
            });
            
            setIsPaymentLoading(false);
            navigate(`/app/confirmacao-pagamento?pedido=${data.pedido.id}&status=aguardando&metodo=cartao`);
          }
        }, 20000);        
        setCustomerData(paymentData);  // ✅ Dados diretos do modal
        toast({
          title: "Processando pagamento",
          description: "Aguarde enquanto confirmamos seu pagamento com o banco...",
        });
      }

    } catch (error: any) {
      console.error('Erro ao processar pagamento:', error);
      toast({
        variant: "destructive",
        title: "Erro no pagamento",
        description: error.message || "Ocorreu um erro ao processar o pagamento. Tente novamente.",
      });

      // Encerrar modal de carregamento e limpar timeout em caso de erro
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsPaymentLoading(false);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-card rounded-lg p-6 border border-border">
      <h2 className="text-xl font-semibold text-foreground mb-4">
        Resumo do Pedido
      </h2>
      
      {/* Timer de Countdown */}
      <div className="flex justify-center mb-6">
        <CountdownTimer 
          initialMinutes={15}
          onExpire={() => console.log('Oferta expirada!')}
          data-testid="cart-countdown-timer"
        />
      </div>
      
      {/* Destaque da Oferta */}
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-l-4 border-yellow-400 p-4 mb-4 rounded-r-lg">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100">
            🎁 OFERTA ESPECIAL
          </Badge>
        </div>
        <p className="text-sm font-medium text-foreground">
          Você está economizando <span className="text-green-600 dark:text-green-400 font-bold">{formatCurrency(discount)}</span> em bônus gratuitos!
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {bonusCount} produtos extras incluídos gratuitamente
        </p>
      </div>

      {/* Resumo Financeiro */}
      <div className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Valor total dos produtos:</span>
          <span className="line-through text-muted-foreground">{formatCurrency(originalTotal)}</span>
        </div>
        
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Subtotal a pagar:</span>
          <span className="text-foreground">{formatCurrency(subtotal)}</span>
        </div>
        
        <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
          <span>Desconto (bônus gratuitos):</span>
          <span>-{formatCurrency(discount)}</span>
        </div>
        
        <div className="border-t border-border pt-3">
          <div className="flex justify-between text-lg font-semibold">
            <span className="text-foreground">Total:</span>
            <span className="text-foreground">{formatCurrency(total)}</span>
          </div>
        </div>
      </div>

      {/* Informações de Entrega */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <strong>📧 Entrega Digital:</strong> Seus produtos serão enviados por email e WhatsApp após a confirmação do pagamento.
        </p>
      </div>

      {/* Botão de Checkout */}
      <Button 
        onClick={handleCheckout}
        className="w-full mt-6 h-12 text-lg font-semibold"
        size="lg"
        disabled={isProcessing}
        data-testid="cart-finalizar-pedido"
      >
        {isProcessing ? "Processando..." : `Finalizar Pedido - ${formatCurrency(total)}`}
      </Button>

      <p className="text-xs text-muted-foreground text-center mt-2">
        Pagamento seguro processado pelo Asaas
      </p>

      {/* Modal de Pagamento */}
      <PaymentMethodModal
        open={isPaymentModalOpen}
        onOpenChange={setIsPaymentModalOpen}
        onConfirm={handlePaymentConfirm}
        isProcessing={isProcessing}
      />

      {/* Modal de Loading do Pagamento */}
      <PaymentLoadingModal 
        open={isPaymentLoading}
        isPixPayment={currentPaymentMethod === 'pix'}
      />

      {/* Modal PIX */}
      {pixData && (
        <PixPaymentModal
          open={isPixModalOpen}
          onOpenChange={setIsPixModalOpen}
          pixData={pixData}
        />
      )}

    </div>
  );
}
